import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';

const QuickBooksVendorReport = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);

  const userId = user?.id;

  useEffect(() => {
    if (userId) {
      loadConnections();
    }
  }, [userId]);

  const loadConnections = async () => {
    try {
      const { default: composioService } = await import('../services/composioService.js');
      const response = await composioService.getConnections(userId);
      const quickbooksConnections = response.connections?.filter(conn => 
        conn.appName === 'QUICKBOOKS' && conn.status === 'ACTIVE'
      ) || [];
      setConnections(quickbooksConnections);
      
      if (quickbooksConnections.length > 0) {
        setSelectedConnection(quickbooksConnections[0]);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      setError('Failed to load QuickBooks connections');
    }
  };

  const handleGetVendorData = async () => {
    if (!userId) {
      setError('User ID not found');
      return;
    }

    setLoading(true);
    setError(null);
    setVendorData(null);

    try {
      const { default: composioService } = await import('../services/composioService.js');
      const data = await composioService.getVendorBalanceDetails(userId);
      setVendorData(data);
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      setError(`Failed to fetch vendor data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (!userId) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">QuickBooks Vendor Report</h3>
        <p className="text-gray-600">Please log in to access QuickBooks data.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">QuickBooks Vendor Balance Report</h3>
      
      {/* Connection Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select QuickBooks Connection:
        </label>
        {connections.length > 0 ? (
          <select
            value={selectedConnection?.id || ''}
            onChange={(e) => {
              const connection = connections.find(conn => conn.id === e.target.value);
              setSelectedConnection(connection);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {connections.map(conn => (
              <option key={conn.id} value={conn.id}>
                {conn.appName} - {conn.status} (ID: {conn.id.slice(0, 8)}...)
              </option>
            ))}
          </select>
        ) : (
          <p className="text-gray-600">No active QuickBooks connections found.</p>
        )}
      </div>

      {/* Action Button */}
      <div className="mb-6">
        <button
          onClick={handleGetVendorData}
          disabled={loading || !userId}
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 ${
            loading || !userId
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
          }`}
        >
          {loading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Loading Vendor Data...
            </div>
          ) : (
            'Get Vendor Balance Details'
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Summary */}
      {vendorData && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Total Vendors</p>
              <p className="text-2xl font-bold text-blue-900">{vendorData.summary.totalVendors}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Total Bills</p>
              <p className="text-2xl font-bold text-green-900">{vendorData.summary.totalBills}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm text-red-600 font-medium">Total Unpaid Amount</p>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(vendorData.summary.totalUnpaidAmount)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Details */}
      {vendorData && vendorData.data && (
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-4">Vendor Details</h4>
          <div className="space-y-6">
            {vendorData.data.map((vendorInfo, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                {/* Vendor Info */}
                <div className="mb-4">
                  <h5 className="text-lg font-semibold text-gray-900 mb-2">{vendorInfo.vendor.name}</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <p><strong>Email:</strong> {vendorInfo.vendor.email || 'N/A'}</p>
                      <p><strong>Phone:</strong> {vendorInfo.vendor.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p><strong>Address:</strong> {vendorInfo.vendor.address || 'N/A'}</p>
                      <p><strong>City:</strong> {vendorInfo.vendor.city || 'N/A'}, {vendorInfo.vendor.state || 'N/A'} {vendorInfo.vendor.zip || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Balance Summary */}
                <div className="mb-4">
                  <h6 className="text-md font-semibold text-gray-800 mb-2">Balance Summary</h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Total Bills</p>
                      <p className="text-lg font-semibold">{vendorInfo.balance.totalBills}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-lg font-semibold">{formatCurrency(vendorInfo.balance.totalAmount)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Paid Bills</p>
                      <p className="text-lg font-semibold text-green-600">{vendorInfo.balance.paidBills}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Unpaid Amount</p>
                      <p className="text-lg font-semibold text-red-600">{formatCurrency(vendorInfo.balance.unpaidAmount)}</p>
                    </div>
                  </div>
                </div>

                {/* Bills Table */}
                {vendorInfo.bills.length > 0 && (
                  <div>
                    <h6 className="text-md font-semibold text-gray-800 mb-2">Bills</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill #</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {vendorInfo.bills.map((bill, billIndex) => (
                            <tr key={billIndex} className={bill.status === 'Paid' ? 'bg-green-50' : 'bg-red-50'}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{bill.docNumber || 'N/A'}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{formatDate(bill.txnDate)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{formatDate(bill.dueDate)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(bill.totalAmt)}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(bill.balance)}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  bill.status === 'Paid' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {bill.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickBooksVendorReport;
